import React from "react";
import { Box, Flex, Text, VStack, Center, useColorMode, Badge } from "@chakra-ui/react";
import type { IconType } from "react-icons";
import { CheckInCheckOutData } from "./DonutChartStatus";
import {
  MdLocationOn,
  MdLogout,
  MdLogin,
  MdTimer,
  MdInfoOutline,
  MdWork,
  MdHome,
  MdAutoMode,
} from "react-icons/md";
import { IconWrapper } from "../../components/IconWrapper";

interface Props {
  entries: CheckInCheckOutData[];
}

const normalizeDuration = (raw: string): string => {
  if (!raw) return "—";
  if (raw.includes("hr")) return raw;
  const minMatch = raw.match(/^(\d+)\s*min$/);
  if (minMatch) {
    const totalMin = parseInt(minMatch[1], 10);
    const hrs = Math.floor(totalMin / 60);
    const mins = totalMin % 60;
    return `${hrs} hr ${mins} min`;
  }
  return raw;
};

const formatDateTime12Hr = (dateInput: string | Date | null | undefined): string => {
  if (!dateInput) return "—";
  const date = dateInput instanceof Date ? dateInput : new Date(dateInput);
  if (isNaN(date.getTime())) return "—";

  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();

  let hours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const seconds = String(date.getSeconds()).padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";
  
  hours = hours % 12;
  hours = hours ? hours : 12; // the hour '0' should be '12'
  const formattedHours = String(hours).padStart(2, "0");

  return `${day}/${month}/${year}, ${formattedHours}:${minutes}:${seconds} ${ampm}`;
};

const InfoRow: React.FC<{
  icon: IconType;
  iconColor: string;
  label: string;
  value: React.ReactNode;
  isDark: boolean;
}> = ({ icon, iconColor, label, value, isDark }) => (
  <Flex align="flex-start" gap={2} w="100%">
    <Box flexShrink={0} mt="2px">
      <IconWrapper icon={icon} size={16} className={iconColor} />
    </Box>
    <Text fontSize="xs" lineHeight="1.5" color={isDark ? "gray.300" : "gray.700"}>
      <Text as="span" fontWeight="bold" color={isDark ? "gray.100" : "gray.800"}>
        {label}:{" "}
      </Text>
      {value}
    </Text>
  </Flex>
);

const DetailLogList: React.FC<Props> = ({ entries }) => {
  const { colorMode } = useColorMode();
  const isDark = colorMode === "dark";

  // Sort entries by check-in time descending (newest first)
  const sortedEntries = [...entries].sort((a, b) => {
    const timeA = a.checkInTime ? new Date(a.checkInTime).getTime() : 0;
    const timeB = b.checkInTime ? new Date(b.checkInTime).getTime() : 0;
    return timeB - timeA;
  });

  return (
    <Box
      maxH="40vh"
      overflowY="auto"
      p={1}
      borderRadius="md"
      bg="zs.card"
    >
      {sortedEntries.length === 0 ? (
        <Center py={8} flexDirection="column" color="gray.400">
          <IconWrapper icon={MdInfoOutline} size={24} className="mb-2 text-[#B59143]" />
          <Text fontSize="sm" mt={2}>No entries found.</Text>
        </Center>
      ) : (
        <VStack spacing={3} align="stretch">
          {sortedEntries.map((entry, idx) => {
            const isAutoCheckout = entry.checkOutAddress === "autocheckout";

            const duration = entry.isCheckedOut
              ? normalizeDuration(entry.duration || "")
              : (() => {
                  const now = new Date();
                  const checkIn = new Date(entry.checkInTime);
                  const diffMs = now.getTime() - checkIn.getTime();
                  const h = Math.floor(diffMs / 1000 / 60 / 60);
                  const m = Math.floor((diffMs / 1000 / 60) % 60);
                  return `${h} hr ${m} min`;
                })();

            const checkoutLabel = entry.isCheckedOut
              ? formatDateTime12Hr(entry.checkOutTime)
              : "Not checked out yet";

            return (
              <Box
                key={idx}
                px={3}
                py={3}
                borderRadius="lg"
                bg={isDark ? "rgba(255,255,255,0.04)" : "gray.50"}
              >
                <VStack align="stretch" spacing={2}>

                  {/* ── Row 1: Check-In / Check-Out ── */}
                  <Flex gap={4} wrap="wrap">
                    <Flex align="flex-start" gap={2} flex="1" minW="160px">
                      <Box flexShrink={0} mt="2px">
                        <IconWrapper icon={MdLogin} size={16} className="text-blue-500" />
                      </Box>
                      <Text fontSize="xs" lineHeight="1.5" color={isDark ? "gray.300" : "gray.700"}>
                        <Text as="span" fontWeight="bold" color={isDark ? "gray.100" : "gray.800"}>
                          Check-In:{" "}
                        </Text>
                        {formatDateTime12Hr(entry.checkInTime)}
                      </Text>
                    </Flex>

                    <Flex align="flex-start" gap={2} flex="1" minW="160px">
                      <Box flexShrink={0} mt="2px">
                        <IconWrapper
                          icon={isAutoCheckout ? MdAutoMode : MdLogout}
                          size={16}
                          className={isAutoCheckout ? "text-orange-500" : "text-red-500"}
                        />
                      </Box>
                      <Text fontSize="xs" lineHeight="1.5" color={isDark ? "gray.300" : "gray.700"}>
                        <Text as="span" fontWeight="bold" color={isDark ? "gray.100" : "gray.800"}>
                          Check-Out:{" "}
                        </Text>
                        {checkoutLabel}
                        {isAutoCheckout && (
                          <Badge
                            ml={1}
                            colorScheme="orange"
                            fontSize="9px"
                            borderRadius="full"
                            px={1.5}
                            py={0.5}
                            verticalAlign="middle"
                          >
                            Auto
                          </Badge>
                        )}
                      </Text>
                    </Flex>
                  </Flex>

                  {/* ── Row 2: Duration / Work Mode ── */}
                  <Flex gap={4} wrap="wrap">
                    <Flex align="flex-start" gap={2} flex="1" minW="140px">
                      <Box flexShrink={0} mt="2px">
                        <IconWrapper icon={MdTimer} size={16} className="text-gray-500" />
                      </Box>
                      <Text fontSize="xs" lineHeight="1.5" color={isDark ? "gray.300" : "gray.700"}>
                        <Text as="span" fontWeight="bold" color={isDark ? "gray.100" : "gray.800"}>
                          Duration:{" "}
                        </Text>
                        {duration}
                        {!entry.isCheckedOut && (
                          <Badge ml={1} colorScheme="green" fontSize="9px" borderRadius="full" px={1.5} py={0.5} verticalAlign="middle">
                            Live
                          </Badge>
                        )}
                      </Text>
                    </Flex>

                    {entry.workMode && (
                      <Flex align="flex-start" gap={2} flex="1" minW="120px">
                        <Box flexShrink={0} mt="2px">
                          <IconWrapper
                            icon={entry.workMode === "Home" ? MdHome : MdWork}
                            size={16}
                            className="text-amber-600"
                          />
                        </Box>
                        <Text fontSize="xs" lineHeight="1.5" color={isDark ? "gray.300" : "gray.700"}>
                          <Text as="span" fontWeight="bold" color={isDark ? "gray.100" : "gray.800"}>
                            Work Mode:{" "}
                          </Text>
                          {entry.workMode}
                        </Text>
                      </Flex>
                    )}
                  </Flex>

                  {/* ── Divider ── */}
                  <Box borderTopWidth="1px" borderColor={isDark ? "rgba(255,255,255,0.06)" : "gray.100"} />

                  {/* ── Row 3: Check-In Address ── */}
                  <InfoRow
                    icon={MdLocationOn}
                    iconColor="text-green-600"
                    label="Check-In Address"
                    value={entry.checkInAddress || "—"}
                    isDark={isDark}
                  />

                  {/* ── Row 4: Check-Out Address ── */}
                  <InfoRow
                    icon={MdLocationOn}
                    iconColor={isAutoCheckout ? "text-orange-500" : "text-gray-500"}
                    label="Check-Out Address"
                    value={
                      isAutoCheckout ? (
                        <Text as="span" color="orange.500" fontWeight="semibold">
                          Auto Checkout
                        </Text>
                      ) : (
                        entry.checkOutAddress || "—"
                      )
                    }
                    isDark={isDark}
                  />

                  {/* ── Row 5: Task Update ── */}
                  {entry.taskUpdate && (() => {
                    const sentences = entry.taskUpdate
                      .split(/\d+\.\s*/)
                      .filter((s: string) => s.trim());
                    return (
                      <Flex align="flex-start" gap={2} w="100%">
                        <Box flexShrink={0} mt="2px">
                          <IconWrapper icon={MdInfoOutline} size={16} className="text-blue-500" />
                        </Box>
                        <Box w="100%">
                          <Text fontSize="xs" fontWeight="bold" color={isDark ? "gray.100" : "gray.800"} mb={1}>
                            Task Detail:
                          </Text>
                          <ol className="list-decimal pl-4 space-y-0.5">
                            {sentences.map((sentence: string, i: number) => (
                              <li key={i} className={`text-xs ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                                {sentence.trim()}
                              </li>
                            ))}
                          </ol>
                        </Box>
                      </Flex>
                    );
                  })()}

                </VStack>
              </Box>
            );
          })}
        </VStack>
      )}
    </Box>
  );
};

export default DetailLogList;
